import React from "react";
import { Globe, Download, ExternalLink, Clock, BookOpen, Server } from "lucide-react";
import { ToolCardShell, type CardStatus } from "../tool";
import { ToolInput, ToolOutput } from "../tool";
import type { ToolCardComponentProps } from "../tool-registry";

interface SearchResult {
  title?: string;
  url?: string;
  description?: string;
}

interface SearchMeta {
  search_engine?: string;
  execution_time?: number;
  search_type?: string;
  cached?: boolean;
}

const SEARCH_ENGINE_LABELS: Record<string, string> = {
  "bailian-mcp": "百练",
  "tavily-mcp": "Tavily",
  "qianfan-api": "千帆",
  "shuqi-mcp": "书旗",
  bailian: "百练",
  tavily: "Tavily",
  qianfan: "千帆",
  shuqi: "书旗",
};

function extractSearchResults(result: unknown): SearchResult[] {
  if (Array.isArray(result)) return result;
  if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (Array.isArray(r.results)) return r.results;
    if (Array.isArray(r.items)) return r.items;
  }
  return [];
}

function extractSearchMeta(result: unknown): SearchMeta {
  if (typeof result === "object" && result !== null && !Array.isArray(result)) {
    const r = result as Record<string, unknown>;
    return {
      search_engine: typeof r.search_engine === "string" ? r.search_engine : undefined,
      execution_time: typeof r.execution_time === "number" ? r.execution_time : undefined,
      search_type: typeof r.search_type === "string" ? r.search_type : undefined,
      cached: typeof r.cached === "boolean" ? r.cached : undefined,
    };
  }
  return {};
}

function truncate(text: string, maxLen: number): string {
  return text.length > maxLen ? text.slice(0, maxLen) + "..." : text;
}

const WebSearchSummary: React.FC<{
  args: Record<string, unknown>;
  result: unknown;
  status: CardStatus;
}> = ({ args, result, status }) => {
  const query = String(args.query || "");
  const results = extractSearchResults(result);
  const meta = extractSearchMeta(result);
  const isNovel = meta.search_type === "novel";

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {isNovel && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 px-2 py-0.5 text-orange-700">
          <BookOpen className="h-3 w-3" />
          小说
        </span>
      )}
      {query && (
        <span className="truncate max-w-[200px]">
          关键词: <span className="text-foreground font-medium">{query}</span>
        </span>
      )}
      {status === "executing" && <span className="text-sky-500">搜索中...</span>}
      {status === "success" && results.length > 0 && (
        <span>{results.length} 条结果</span>
      )}
      {meta.search_engine && meta.search_engine !== "unknown" && (
        <span className="inline-flex items-center gap-0.5 text-muted-foreground/70">
          <Server className="h-3 w-3" />
          {SEARCH_ENGINE_LABELS[meta.search_engine] || meta.search_engine}
        </span>
      )}
      {meta.execution_time != null && (
        <span className="inline-flex items-center gap-0.5 text-muted-foreground/70">
          <Clock className="h-3 w-3" />
          {meta.execution_time.toFixed(2)}s
        </span>
      )}
      {meta.cached && (
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">缓存</span>
      )}
    </div>
  );
};

const WebSearchResults: React.FC<{ result: unknown }> = ({ result }) => {
  const results = extractSearchResults(result);
  if (results.length === 0) return null;

  return (
    <div className="px-4 py-2 space-y-2">
      {results.map((item, idx) => (
        <div key={idx} className="rounded-md border border-border/50 p-2">
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
            >
              {item.title || item.url}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-sm font-medium">{item.title}</span>
          )}
          {item.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>
          )}
        </div>
      ))}
    </div>
  );
};

const WebFetchSummary: React.FC<{
  args: Record<string, unknown>;
  result: unknown;
}> = ({ args, result }) => {
  const url = String(args.url || "");
  const maxLength = args.max_length as number | undefined;

  let charCount = 0;
  if (typeof result === "string") charCount = result.length;
  else if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (typeof r.content === "string") charCount = r.content.length;
    if (typeof r.text === "string") charCount = r.text.length;
  }

  return (
    <div className="px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {url && (
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(url)}
          className="truncate max-w-[240px] text-foreground hover:text-primary font-mono"
          title="点击复制 URL"
        >
          {truncate(url, 60)}
        </button>
      )}
      {charCount > 0 && (
        <span>获取内容: {charCount.toLocaleString()} 字{maxLength ? ` / 限制 ${maxLength.toLocaleString()} 字` : ""}</span>
      )}
    </div>
  );
};

const WebFetchPreview: React.FC<{ result: unknown }> = ({ result }) => {
  let content = "";
  if (typeof result === "string") content = result;
  else if (typeof result === "object" && result !== null) {
    const r = result as Record<string, unknown>;
    if (typeof r.content === "string") content = r.content;
    if (typeof r.text === "string") content = r.text;
  }
  if (!content) return null;

  return (
    <div className="mx-4 mb-2 text-xs whitespace-pre-wrap font-mono bg-muted/50 rounded p-2 max-h-[200px] overflow-y-auto">
      {content}
    </div>
  );
};

export const ExternalCard: React.FC<ToolCardComponentProps> = ({ toolCall }) => {
  const cardStatus: CardStatus = toolCall.status || "detected";
  const toolName = toolCall.toolName;
  const args = toolCall.args || {};
  const result = toolCall.result;

  if (toolName === "web_fetch") {
    return (
      <ToolCardShell
        toolName="网页抓取"
        status={cardStatus}
        icon={<Download className="h-4 w-4" />}
        iconColor="text-teal-500"
        defaultExpanded={false}
      >
        <WebFetchSummary args={args} result={result} />
        <WebFetchPreview result={result} />
        {toolCall.args && Object.keys(toolCall.args).length > 0 && (
          <ToolInput data={toolCall.args} />
        )}
        {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
          <ToolOutput data={toolCall.result} error={toolCall.error} />
        ) : null}
      </ToolCardShell>
    );
  }

  const resultObj = typeof result === "object" && result !== null && !Array.isArray(result)
    ? (result as Record<string, unknown>)
    : null;
  const searchType = resultObj?.search_type as string | undefined;
  const searchEngine = resultObj?.search_engine as string | undefined;
  const isNovel = searchType === "novel";
  const toolLabel = isNovel ? "小说搜索" : "网络搜索";
  const engineLabel = searchEngine && searchEngine !== "unknown"
    ? (SEARCH_ENGINE_LABELS[searchEngine] || searchEngine)
    : undefined;

  return (
    <ToolCardShell
      toolName={toolLabel}
      status={cardStatus}
      icon={isNovel ? <BookOpen className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
      iconColor={isNovel ? "text-orange-500" : "text-sky-500"}
      defaultExpanded={false}
    >
      <WebSearchSummary args={args} result={result} status={cardStatus} />
      {cardStatus === "success" && <WebSearchResults result={result} />}
      {cardStatus === "failed" && engineLabel && (
        <div className="px-4 py-2 flex items-center gap-1.5 text-xs text-destructive/80">
          <Server className="h-3 w-3" />
          <span>供应商: {engineLabel}</span>
        </div>
      )}
      {toolCall.args && Object.keys(toolCall.args).length > 0 && (
        <ToolInput data={toolCall.args} />
      )}
      {(toolCall.result !== undefined && toolCall.result !== null) || toolCall.error ? (
        <ToolOutput data={toolCall.result} error={toolCall.error} />
      ) : null}
    </ToolCardShell>
  );
};
